/**
 * Custom Sticky Add to Cart — A/B test (round 1)
 *
 * Two independent instances can exist per product (desktop / mobile), each
 * toggled on separately in the theme editor. Both:
 *   - Reveal only once the main PDP "Add to Cart" button (#AddToCart) has
 *     scrolled out of view above the viewport, and hide again once it's
 *     back in view.
 *   - Stay in sync with whichever variant is selected anywhere on the page
 *     (main form, native sticky drawer, or this bar's own dropdowns) by
 *     listening for the theme's existing "product:variant-change" event,
 *     rather than relying solely on the theme's built-in cross-instance
 *     sync (which only guarantees updating one other <variant-selects> —
 *     see note below).
 */
if (!customElements.get("sticky-atc-custom")) {
  class StickyAtcCustom extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.priceEl = this.querySelector(".sticky-atc-custom__price");
      this.sizeEl = this.querySelector(".sticky-atc-custom__size-locked");
      this.button = this.querySelector(".sticky-atc-custom__button");
      this.sizeOptionPosition = this.dataset.sizeOptionPosition
        ? parseInt(this.dataset.sizeOptionPosition, 10)
        : null;

      this.setupScrollTrigger();
      this.setupVariantSync();
      this.setupChangeSelectionLink();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }

      if (this.handleVariantUpdate) {
        document.removeEventListener(
          "product:variant-change",
          this.handleVariantUpdate,
        );
      }

      if (this.handleVariantSelectSync) {
        document.removeEventListener(
          "product:variant-change",
          this.handleVariantSelectSync,
        );
      }
    }

    setupScrollTrigger() {
      const trigger = document.getElementById("AddToCart");
      if (!trigger) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.remove("sticky-atc-custom--visible");
            } else if (entry.boundingClientRect.top < 0) {
              // Only reveal when the button has scrolled OFF THE TOP —
              // not before the page has scrolled that far yet.
              this.classList.add("sticky-atc-custom--visible");
            }
          });
        },
        { threshold: 0 },
      );

      this.observer.observe(trigger);
    }

    setupVariantSync() {
      this.handleVariantUpdate = (event) => {
        const detail = event.detail || {};
        if (String(detail.sectionId) !== String(this.section)) return;

        this.updateFromVariant(detail.variant);
      };

      document.addEventListener(
        "product:variant-change",
        this.handleVariantUpdate,
      );

      const variantSelects = this.querySelector("variant-selects");

      if (variantSelects) {
        this.handleVariantSelectSync = (event) => {
          const detail = event.detail || {};
          if (String(detail.sectionId) !== String(this.section)) return;
          if (!detail.variant) return;

          this.syncOwnSelects(variantSelects, detail.variant);
          variantSelects.updateOptions();
          variantSelects.updateMasterId();
          variantSelects.setDisabled();
          this.pushToMainForm(detail.variant);
        };

        document.addEventListener(
          "product:variant-change",
          this.handleVariantSelectSync,
        );
      }
    }

    pushToMainForm(variant) {
      if (!variant) return;
      const main = document.querySelector(
        `variant-selects[data-section="${this.section}"]:not([data-sticky])`,
      );
      if (!main) return;

      const fieldsets = main.querySelectorAll("fieldset[data-index]");
      let changed = false;
      fieldsets.forEach((fieldset) => {
        const match = fieldset.dataset.index.match(/option(\d+)/);
        if (!match) return;
        const position = parseInt(match[1], 10);
        const value = variant["option" + position];
        if (value === undefined) return;

        const select = fieldset.querySelector("select");
        if (select) {
          // Dropdown-style picker
          if (select.value !== value) {
            select.value = value;
            changed = true;
          }
          return;
        }

        // Button/radio-style picker (this theme's main buy box uses this —
        // picker_type: "button" — one <input type="radio"> per value,
        // styled to look like a clickable button rather than a dropdown)
        const radio = fieldset.querySelector(
          `input[type="radio"][value="${CSS.escape(value)}"]`,
        );
        if (radio && !radio.checked) {
          radio.checked = true;
          changed = true;
        }
      });

      // Only fire main's own change handling (price, ATC state, etc.) if
      // something actually needed updating — avoids redundant work when
      // this bar's change matches what the main form already shows.
      if (changed) {
        main.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    syncOwnSelects(variantSelects, variant) {
      const fieldsets = variantSelects.querySelectorAll("fieldset[data-index]");
      fieldsets.forEach((fieldset) => {
        const match = fieldset.dataset.index.match(/option(\d+)/);
        if (!match) return;
        const position = parseInt(match[1], 10);
        const value = variant["option" + position];
        if (value === undefined) return;

        const select = fieldset.querySelector("select");
        if (select && select.value !== value) select.value = value;
      });
    }

    updateFromVariant(variant) {
      if (!variant) {
        if (this.priceEl) {
          this.priceEl.style.display = "none";
        }

        if (this.button) {
          this.button.disabled = true;
          this.button.classList.add("sold-out");

          const label = this.button.querySelector(
            ".sticky-atc-custom__button-text",
          );

          if (label) {
            label.dataset.content = this.button.dataset.labelSoldOut;
          }

          this.button.setAttribute(
            "aria-label",
            this.button.dataset.labelSoldOut,
          );
        }

        return;
      }

      if (this.priceEl) {
        this.priceEl.style.display = "";
        const format =
          (window.theme &&
            window.theme.settings &&
            window.theme.settings.money_with_currency_format) ||
          "${{amount}}";
        const current = this.priceEl.querySelector(
          ".sticky-atc-custom__price-current",
        );
        const compare = this.priceEl.querySelector(
          ".sticky-atc-custom__price-compare",
        );

        if (current && typeof formatMoney === "function") {
          current.textContent = formatMoney(variant.price, format);
        }

        if (variant.compare_at_price > variant.price) {
          if (compare && typeof formatMoney === "function") {
            compare.textContent = formatMoney(variant.compare_at_price, format);
            compare.style.display = "";
          }
        } else if (compare) {
          compare.style.display = "none";
        }
      }

      if (this.sizeEl && this.sizeOptionPosition) {
        const value = variant["option" + this.sizeOptionPosition];
        if (value) this.sizeEl.textContent = value;
      }

      if (this.button) {
        const available = !!variant.available;
        this.button.disabled = !available;
        this.button.classList.toggle("sold-out", !available);
        const label = this.button.querySelector(
          ".sticky-atc-custom__button-text",
        );
        const buttonLabel = available
          ? this.button.dataset.labelAdd
          : this.button.dataset.labelSoldOut;

        if (label) {
          label.dataset.content = buttonLabel;
        }

        this.button.setAttribute("aria-label", buttonLabel);
      }
    }

    setupChangeSelectionLink() {
      const link = this.querySelector("[data-change-selection]");
      if (!link) return;

      link.addEventListener("click", (event) => {
        const target = document.getElementById("AddToCart");
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }

  customElements.define("sticky-atc-custom", StickyAtcCustom);
}
